import { Box3, InstancedMesh, Matrix4, Vector3, Group, Mesh } from 'three';
import { Loader3d } from '../utils/loader3d';
import { Merger } from '../utils/merger';
import { DIRECTION } from '../../../js/structures/aqua/gridAqua';
import { Utils3d } from '../utils/utils3d';

/// class to draw each 3D object that comes in the answers
export class Object3D {

	/// Aqua Object structure:
	///	{
	///		'codaqua': 'ASYSTARTER',
	///		'file': 'poste_28398dd5fb.glb',
	///		'largo': 0,
	///		'ancho': 0,
	///		'alto': 0,
	///		'escalablealtura': true,
	///		'escalableancho': false,
	///		'escalablelargo': false,
	///		'desplazaraltura': false,
	///		'preguntasadmitidas': 'invernadero',
	///		'tipodibujo': 'postes',
	///		'nombre': 'Poste'
	///	}

	
	constructor( scene, data ) {

		this.data = data;
		this.scene = scene;

		this._loader = new Loader3d();
		this._backendUrl = sessionStorage.getItem( 'backendUrl' );

		this._instanced = true;
	}

	async draw( object, answer ) {
		
		this.object = object;
		this.answer = answer;

		//get file and download
		const separator = this.object.file.url.startsWith('/') ? '' : '/';
		let obj3d = await this._loader.load( `${this._backendUrl}${separator}${this.object.file.url}` );
		if( !obj3d || !obj3d.scene ) { console.error( `missing 3d file for ${this.object.nombre}` ); return; }

		obj3d = obj3d.scene;

		switch( this.object.preguntasadmitidas ) {
			case PREGUNTASADMITIDAS.INVERNADERO:
				this.#drawInvernadero( obj3d );
				break;
			case PREGUNTASADMITIDAS.LATERALES:
				this.#drawLaterales( obj3d );
				break;
			case PREGUNTASADMITIDAS.FRONTAL:
				this.#drawFrontal( obj3d );
				break;
			case PREGUNTASADMITIDAS.CAPILLA:
				this.#drawCapilla( obj3d );
				break;
			case PREGUNTASADMITIDAS.ZONA:
				this.#drawZona( obj3d );
				break;
			case PREGUNTASADMITIDAS.PUNTOFIJO:
				this.#drawPuntoFijo( obj3d );
				break;
			case PREGUNTASADMITIDAS.PUNTOFIJOZONA:
				this.#drawPuntoFijoZona( obj3d );
				break;
		}
		
		if( this.object.desplazaraltura && this.mesh ) {
			this.mesh.position.y = this.data.alturaInvernadero;
		}

		if( this.mesh ) {

			this.scene.add( this.mesh );

			Utils3d.makePerformant( this.mesh );
		}
	}

	#drawInvernadero( obj3d ) {
		
		this.clearMesh();

		const grid = this.data.grid;
		const helper = new Vector3();
		
		//draw invernadero
		switch ( this.object.tipodibujo ) {
			case TIPODIBUJO.CASILLA: { //one per cell. TODO: we set it in the middle of the cell, IS IT CORRECT?

				const mesh = new Merger().merge( obj3d );
				const rotation = mesh.quaternion;
				const draws = [];

				this.mesh = new InstancedMesh( mesh.geometry, mesh.material, grid.cells.length );
				for( let i = 0; i < grid.cells.length; i++ ) {
					const cell = grid.cells[i];
					helper.set( -cell.MIDAncho / 2, 0, cell.MIDLargo / 2 );

					if( !this.#isOKToDraw( cell ) ) continue;
					draws.push( new Matrix4().compose(
						grid.cells[i].position.clone().add( helper ),
						rotation,
						this.#getObjScale( mesh, cell ) )
					);
				}
				this.#createMesh( mesh, draws );
			} break;
			case TIPODIBUJO.POSTES: { //4 per cell, in each corner

				const mesh = new Merger().merge( obj3d );

				const draws = [];
				const rotation = mesh.quaternion;
				
				for( let i = 0; i < grid.cells.length; i++ ) {
					const cell = grid.cells[i];
					
					if( !this.#isOKToDraw( cell ) ) continue;

					const scale = this.#getObjScale( mesh, cell );
					//LEFT TOP
					if( !cell.MIDIzquierda && !cell.MIDArriba && !cell.MIDEsquiNOROEST ) {
						draws.push( new Matrix4().compose(
							cell.position.clone().add( new Vector3( -cell.MIDAncho / 2, 0, -cell.MIDLargo / 2 ) ),
							rotation,
							scale
						) );		
					}
					//RIGHT TOP
					if( !cell.MIDArriba ) {
						draws.push( new Matrix4().compose(
							cell.position.clone().add( new Vector3( cell.MIDAncho / 2, 0, -cell.MIDLargo / 2 ) ),
							rotation,
							scale
						) );
					}
					//LEFT BOTTOM
					if( !cell.MIDIzquierda ) {
						draws.push( new Matrix4().compose(
							cell.position.clone().add( new Vector3( -cell.MIDAncho / 2, 0, cell.MIDLargo / 2 ) ),
							rotation,
							scale
						) );
					}
					//RIGHT BOTTOM
					draws.push( new Matrix4().compose(
						cell.position.clone().add( new Vector3( cell.MIDAncho / 2, 0, cell.MIDLargo / 2 ) ),
						rotation,
						scale
					) );
				}

				this.#createMesh( mesh, draws );

			} break;
			case TIPODIBUJO.HORIZONTALES: { // 2 per cell, on the top and bottom sides

				const mesh = new Merger().merge( obj3d );

				const draws = [];
				const rotation = mesh.quaternion;
				
				for( let i = 0; i < grid.cells.length; i++ ) {
					const cell = grid.cells[i];

					if( !this.#isOKToDraw( cell ) ) continue;

					const scale = this.#getObjScale( mesh, cell );
					//TOP SIDE
					if( !cell.MIDArriba ) {
						helper.set( -cell.MIDAncho / 2, 0, -cell.MIDLargo / 2 );
						draws.push( new Matrix4().compose(
							cell.position.clone().add( helper ),
							rotation,
							scale
						) );
					}

					helper.set( -cell.MIDAncho / 2, 0, cell.MIDLargo / 2 );
					//BOTTOM SIDE
					draws.push( new Matrix4().compose(
						cell.position.clone().add( helper ),
						rotation,
						scale
					) );					
				}

				this.#createMesh( mesh, draws );
			} break;
			case TIPODIBUJO.VERTICALES: { // 2 per cell, on left and right sides

				const mesh = new Merger().merge( obj3d );

				const draws = [];
				const rotation = mesh.quaternion;
				
				for( let i = 0; i < grid.cells.length; i++ ) {
					const cell = grid.cells[i];
					
					if( !this.#isOKToDraw( cell ) ) continue;

					const scale = this.#getObjScale( mesh, cell );
					//LEFT SIDE
					if( !cell.MIDIzquierda ) {
						helper.set( -cell.MIDAncho / 2, 0, cell.MIDLargo / 2 );
						draws.push( new Matrix4().compose(
							cell.position.clone().add( helper ),
							rotation,
							scale
						) );		
					}
					//RIGHT SIDE
					helper.set( cell.MIDAncho / 2, 0, cell.MIDLargo / 2 );
					draws.push( new Matrix4().compose(
						cell.position.clone().add( helper ),
						rotation,
						scale
					) );
				}

				this.#createMesh( mesh, draws );

			} break;			
		}
	}

	#drawLaterales( obj3d ) {

		this.clearMesh();

		const grid = this.data.grid;
		const helper = new Vector3();

		//draw frontales
		switch ( this.object.tipodibujo ) {
			case TIPODIBUJO.CASILLA: { //one per cell that is lateral. TODO: we set it in the middle of the cell, IS IT CORRECT?

				const mesh = new Merger().merge( obj3d );
				
				const draws = [];
				const rotation = mesh.quaternion;

				const cached = {};
				for( let i = 0; i < grid.frontales.length; i++ ) {
					const lateral = grid.laterales[i];
					//on north cells, we set mirror elements and position is not left/down, is left/up
					const dir = lateral.direction === DIRECTION.WEST ? -1 : 1;
					for( let j = 0; j < lateral.cells.length; j++ ) {
						const cell = lateral.cells[j];
						
						if( !this.#isOKToDraw( cell ) ) continue;

						const key = `${cell.MIDFila}_${cell.MIDColumna}`;
						if( cached[key] ) continue;
						cached[key] = true;
						helper.set( dir * cell.MIDAncho / 2, 0, cell.MIDLargo / 2 );
						
						//make mirror in the Z axis for north cells
						const scale = this.#getObjScale( mesh, cell );
						scale.x = scale.x * -dir;

						draws.push( new Matrix4().compose(
							cell.position.clone().add( helper ),
							rotation,
							scale ) );
					}
				}

				this.#createMesh( mesh, draws );
			} break;
			case TIPODIBUJO.POSTES: { //4 per cell, in each corner

				const mesh = new Merger().merge( obj3d );

				const draws = [];
				const rotation = mesh.quaternion;
				
				const freeTop = ( cell, cells, left = true ) => {
					const fila = cell.MIDFila;
					const columna = cell.MIDColumna;
					return !cells.find( c => c.MIDFila == fila - 1 && c.MIDColumna == ( left ? columna - 1 : columna + 1 ) );
				};

				const leftCells = grid.cells.filter( c => !c.MIDIzquierda );
				const rightCells = grid.cells.filter( c => !c.MIDDerecha );
				const addDraw = ( cell, dir ) => {
					//make mirror in the Z axis for north cells
					const scale = this.#getObjScale( mesh, cell );
					scale.x = scale.x * ( dir === DIRECTION.WEST ? 1 : -1 );

					if( dir === DIRECTION.WEST ) {
						//LEFT TOP
						if( freeTop( cell, leftCells ) ) {
							draws.push( new Matrix4().compose(
								cell.position.clone().add( new Vector3( -cell.MIDAncho / 2, 0, -cell.MIDLargo / 2 ) ),
								rotation,
								scale
							) );		
						}
						//LEFT BOTTOM
						draws.push( new Matrix4().compose(
							cell.position.clone().add( new Vector3( -cell.MIDAncho / 2, 0, cell.MIDLargo / 2 ) ),
							rotation,
							scale
						) );
					} else if ( dir === DIRECTION.EAST ) {
						//RIGHT TOP
						if( freeTop( cell, rightCells, false ) ) {
							draws.push( new Matrix4().compose(
								cell.position.clone().add( new Vector3( cell.MIDAncho / 2, 0, -cell.MIDLargo / 2 ) ),
								rotation,
								scale
							) );
						}
						
						//RIGHT BOTTOM
						draws.push( new Matrix4().compose(
							cell.position.clone().add( new Vector3( cell.MIDAncho / 2, 0, cell.MIDLargo / 2 ) ),
							rotation,
							scale
						) );
					}
				};

				const cached = {};
				for( let i = 0; i < grid.laterales.length; i++ ) {
					const lateral = grid.laterales[i];
					for( let j = 0; j < lateral.cells.length; j++ ) {
						const cell = lateral.cells[j];
						const key = `${cell.MIDFila}_${cell.MIDColumna}`;
						if( cached[key] ) continue;
						cached[key] = true;
						addDraw( cell, lateral.direction );
					}
				}
				this.#createMesh( mesh, draws );
			} break;
			case TIPODIBUJO.INICIO: { // 1 per cell per lateral

				const mesh = new Merger().merge( obj3d );
				const rotation = mesh.quaternion;

				const draws = [];

				const cached = {};
				for( let i = 0; i < grid.laterales.length; i++ ) {
					const lateral = grid.laterales[i];
					const dir = lateral.direction === DIRECTION.WEST ? -1 : 1;
					const cell = lateral.cells.find( c => c.MIDFila === lateral.inicio );
					
					if( !this.#isOKToDraw( cell ) ) continue;
					
					const key = `${cell.MIDFila}_${cell.MIDColumna}_${lateral.direction}`;
					if( cached[key] ) continue;
					cached[key] = true;
					
					const scale = this.#getObjScale( mesh, cell );
					scale.x = scale.x * -dir;
					
					helper.set( dir * (cell.MIDAncho / 2), 0, cell.MIDLargo / 2 );
					draws.push( new Matrix4().compose(
						cell.position.clone().add( helper ),
						rotation,
						scale ) );
				}
				this.#createMesh( mesh, draws );

			} break;
			case TIPODIBUJO.FIN: { // 2 per cell, in the middle of the top and bottom sides

				const mesh = new Merger().merge( obj3d );
				const rotation = mesh.quaternion;

				const draws = [];

				const cached = {};
				for( let i = 0; i < grid.laterales.length; i++ ) {
					const lateral = grid.laterales[i];
					const dir = lateral.direction === DIRECTION.WEST ? -1 : 1;
					const cell = lateral.cells.find( c => c.MIDFila === lateral.fin );

					if( !this.#isOKToDraw( cell ) ) continue;

					const key = `${cell.MIDFila}_${cell.MIDColumna}_${lateral.direction}`;
					if( cached[key] ) continue;
					cached[key] = true;
					
					const scale = this.#getObjScale( mesh, cell );
					scale.x = scale.x * -dir;
					
					helper.set( dir * (cell.MIDAncho / 2), 0, cell.MIDLargo / 2 );
					draws.push( new Matrix4().compose(
						cell.position.clone().add( helper ),
						rotation,
						scale ) );
				}
				this.#createMesh( mesh, draws );
			} break;			
		}
	}

	#drawFrontal( obj3d ) {
		this.clearMesh();

		const grid = this.data.grid;
		const helper = new Vector3();

		//draw frontales
		switch ( this.object.tipodibujo ) {
			case TIPODIBUJO.CASILLA: { //one per cell that is frontal

				const mesh = new Merger().merge( obj3d );
				
				const draws = [];
				const rotation = mesh.quaternion;

				const cached = {};
				for( let i = 0; i < grid.frontales.length; i++ ) {
					const frontal = grid.frontales[i];
					//on north cells, we set mirror elements and position is not left/down, is left/up
					const dir = frontal.direction === DIRECTION.NORTH ? -1 : 1;
					for( let j = 0; j < frontal.cells.length; j++ ) {
						const cell = frontal.cells[j];

						if( !this.#isOKToDraw( cell ) ) continue;

						const key = `${cell.MIDFila}_${cell.MIDColumna}`;
						if( cached[key] ) continue;
						cached[key] = true;
						//we set initial position in left/down or left/up
						helper.set( -cell.MIDAncho / 2, 0, dir * cell.MIDLargo / 2 );

						//make mirror in the Z axis for north cells
						const scale = this.#getObjScale( mesh, cell );
						scale.z = scale.z * dir;

						draws.push( new Matrix4().compose(
							cell.position.clone().add( helper ),
							rotation,
							scale ) );
					}
				}
				this.#createMesh( mesh, draws );
			} break;
			case TIPODIBUJO.POSTES: { //2 per cell, in each corner (top corner if NORTH, bottom corner if SOUTH)

				const mesh = new Merger().merge( obj3d );

				const draws = [];
				const rotation = mesh.quaternion;
				
				const freeTop = ( cell, cells, left = true ) => {
					const fila = cell.MIDFila;
					const columna = cell.MIDColumna;
					return !cells.find( c => c.MIDFila == fila - 1 && c.MIDColumna == ( left ? columna - 1 : columna + 1 ) );
				};

				const topCells = grid.cells.filter( c => !c.MIDArriba );
				const bottomCells = grid.cells.filter( c => !c.MIDAbajo );
				const addDraw = ( cell, dir ) => {

					if( !this.#isOKToDraw( cell ) ) return;

					//make mirror in the Z axis for north cells
					const scale = this.#getObjScale( mesh, cell );
					scale.z = scale.z * ( dir === DIRECTION.NORTH ? -1 : 1 );

					//LEFT TOP
					if( dir ===  DIRECTION.NORTH ) {
						if( freeTop( cell, topCells ) ) {
							draws.push( new Matrix4().compose(
								cell.position.clone().add( new Vector3( -cell.MIDAncho / 2, 0, -cell.MIDLargo / 2 ) ),
								rotation,
								scale
							) );		
						}
						//RIGHT TOP
						if( freeTop( cell, bottomCells, false ) ) {
							draws.push( new Matrix4().compose(
								cell.position.clone().add( new Vector3( cell.MIDAncho / 2, 0, -cell.MIDLargo / 2 ) ),
								rotation,
								scale
							) );
						}
					}
					else if( dir === DIRECTION.SOUTH ) {
						//LEFT BOTTOM
						draws.push( new Matrix4().compose(
							cell.position.clone().add( new Vector3( -cell.MIDAncho / 2, 0, cell.MIDLargo / 2 ) ),
							rotation,
							scale
						) );
						//RIGHT BOTTOM
						draws.push( new Matrix4().compose(
							cell.position.clone().add( new Vector3( cell.MIDAncho / 2, 0, cell.MIDLargo / 2 ) ),
							rotation,
							scale
						) );
					}
				};

				const cached = {};
				for( let i = 0; i < grid.frontales.length; i++ ) {
					const frontal = grid.frontales[i];
					for( let j = 0; j < frontal.cells.length; j++ ) {
						const cell = frontal.cells[j];
						
						if( !this.#isOKToDraw( cell ) ) return;

						const key = `${cell.MIDFila}_${cell.MIDColumna}`;
						if( cached[key] ) continue;
						cached[key] = true;
						addDraw( cell, frontal.direction );
					}
				}

				this.#createMesh( mesh, draws );
			} break;
			case TIPODIBUJO.INICIO: { // 1 per cell per lateral

				const mesh = new Merger().merge( obj3d );
				const rotation = mesh.quaternion;

				const draws = [];

				const cached = {};
				for( let i = 0; i < grid.frontales.length; i++ ) {
					const frontal = grid.frontales[i];
					//on north cells, we set mirror elements and position is not left/down, is left/up
					const dir = frontal.direction === DIRECTION.NORTH ? -1 : 1;
					const cell = frontal.cells.find( c => c.MIDColumna === frontal.inicio );

					if( !this.#isOKToDraw( cell ) ) continue;

					const key = `${cell.MIDFila}_${cell.MIDColumna}_${frontal.direction}`;
					if( cached[key] ) continue;
					cached[key] = true;
					
					//make mirror in the Z axis for north cells
					const scale = this.#getObjScale( mesh, cell );
					scale.z = scale.z * dir;

					helper.set( -cell.MIDAncho / 2, 0, dir * cell.MIDLargo / 2 );
					draws.push( new Matrix4().compose(
						cell.position.clone().add( helper ),
						rotation,
						scale ) );
				}
				this.#createMesh( mesh, draws );

			} break;
			case TIPODIBUJO.FIN: { // 2 per cell, in the middle of the top and bottom sides

				const mesh = new Merger().merge( obj3d );
				const rotation = mesh.quaternion;

				const draws = [];

				const cached = {};
				for( let i = 0; i < grid.frontales.length; i++ ) {
					const frontal = grid.frontales[i];
					//on north cells, we set mirror elements and position is not left/down, is left/up
					const dir = frontal.direction === DIRECTION.NORTH ? -1 : 1;
					const cell = frontal.cells.find( c => c.MIDColumna === frontal.fin );

					if( !this.#isOKToDraw( cell ) ) continue;

					const key = `${cell.MIDFila}_${cell.MIDColumna}_${frontal.direction}`;
					if( cached[key] ) continue;
					cached[key] = true;
					
					//make mirror in the Z axis for north cells
					const scale = this.#getObjScale( mesh, cell );
					scale.z = scale.z * dir;

					helper.set( -cell.MIDAncho / 2, 0, dir * cell.MIDLargo / 2 );
					draws.push( new Matrix4().compose(
						cell.position.clone().add( helper ),
						rotation,
						scale ) );
				}
				this.#createMesh( mesh, draws );
			} break;			
		}
	}

	#drawCapilla( obj3d ) {
		//draw capilla
	}

	#drawZona( obj3d ) {
		//draw zona
	}

	#drawPuntoFijo( obj3d ) {
		//draw punto fijo
	}

	#drawPuntoFijoZona( obj3d ) {
		//draw punto fijo zona
	}

	#getObjScale( obj, cell ) {
		const scale = obj.scale.clone();
		
		const bbox = new Box3().setFromObject( obj );
		const size = new Vector3(); bbox.getSize( size );

		scale.x = this.object.escalableancho && cell ? cell.MIDAncho / size.x : scale.x;
		scale.y = this.object.escalablealtura ? this.data.alturaInvernadero / size.y : scale.y;
		scale.z = this.object.escalablelargo && cell ? cell.MIDLargo / size.z : scale.z;

		return scale;
	}

	#isOKToDraw( cell ) {
		if( !cell ) return false;
		
		// Check if MIDAncho & MIDAlto matches with object ancho & alto
		if( this.object.ancho !== 0 && this.object.ancho !== cell.MIDAncho ) return false;
		if( this.object.largo !== 0 && this.object.largo !== cell.MIDLargo ) return false;

		return true;
	}

	#createMesh( mesh, draws ) {
		if( this._instanced ) {
			this.mesh = new InstancedMesh( mesh.geometry, mesh.material, draws.length );
			for( let i = 0; i < draws.length; i++ ) this.mesh.setMatrixAt( i, draws[i] );
		} else {
			this.mesh = new Group();
			for( let i = 0; i < draws.length; i++ ) {
				const m = new Mesh( mesh.geometry, mesh.material );
				
				m.applyMatrix4(draws[i] );
				this.mesh.add( m );
			}
		}
	}

	clearMesh() {
		if( this.mesh ) {
			this.scene.remove( this.mesh );

			Utils3d.dispose3d( this.mesh );
			this.mesh = null;
		}
	}
}

export const PREGUNTASADMITIDAS = {
	INVERNADERO: 'invernadero',
	LATERALES: 'laterales',
	FRONTAL: 'frontal',
	CAPILLA: 'capilla',
	ZONA: 'zona',
	PUNTOFIJO: 'puntofijo',
	PUNTOFIJOZONA: 'puntofijozona'
};

export const TIPODIBUJO = {
	CASILLA: 'casilla',
	POSTES: 'postes',
	HORIZONTALES: 'horizontales',
	VERTICALES: 'verticales',
	INICIO: 'inicio',
	FIN: 'fin',
};